// src/components/Router.tsx
import { ReactNode, createContext, useContext, useState, useEffect } from 'react';

interface RouterContextType {
  currentPath: string;
  navigate: (path: string) => void;
}

const RouterContext = createContext<RouterContextType | undefined>(undefined);

export function Router({ children }: { children: ReactNode }) {
  const [currentPath, setCurrentPath] = useState(window.location.pathname + window.location.search);
 
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname + window.location.search);
      // Scroll to top when navigating back/forward
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

const navigate = (path: string) => {
  // Make sure we're navigating within the same origin
  if (path.startsWith('http') || path.startsWith('//')) {
    window.open(path, '_blank');
    return;
  }
  
  // Check if it's an absolute URL but missing protocol (e.g., "example.com/page")
  if (path.includes('://') === false && path.includes('.') && !path.startsWith('/')) {
    window.open('https://' + path, '_blank');
    return;
  }
  
  // Update URL
  const newUrl = path.startsWith('/') ? path : '/' + path;
  window.history.pushState({}, '', newUrl);
  
  // Force a re-render by updating path
  setCurrentPath(newUrl);
  
  // Scroll to top when navigating
  window.scrollTo(0, 0);
};

  return (
    <RouterContext.Provider value={{ currentPath, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useRouter must be used within Router');
  }
  return context;
}

export function Route({ path, children }: { path: string; children: ReactNode }) {
  const { currentPath } = useRouter();
  
  // Extract just the pathname part for comparison (ignore query params)
  const currentPathname = currentPath.split('?')[0];
  
  // Handle dynamic routes with parameters
  if (path.includes(':')) {
    const pathSegments = path.split('/');
    const currentSegments = currentPathname.split('/');
    
    if (pathSegments.length !== currentSegments.length) {
      return null;
    }
    
    const isMatch = pathSegments.every((segment, index) => {
      return segment.startsWith(':') || segment === currentSegments[index];
    });
    
    return isMatch ? <>{children}</> : null;
  }
  
  return currentPathname === path ? <>{children}</> : null;
}

export function Navigate({ to }: { to: string }) {
  const { navigate } = useRouter();

  useEffect(() => {
    navigate(to);
  }, [to, navigate]);

  return null;
}

export function Link({ to, children, className, onClick, scroll = true }: { 
  to: string; 
  children: ReactNode; 
  className?: string; 
  onClick?: () => void;
  scroll?: boolean;
}) {
  const { navigate } = useRouter();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    if (scroll) {
      window.scrollTo(0, 0);
    }
    if (onClick) onClick();
  };

  return (
    <a href={to} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}

export function useParams() {
  const { currentPath } = useRouter();
  const params: Record<string, string> = {};
  
  // Parse query parameters
  const urlParams = new URLSearchParams(currentPath.split('?')[1] || '');
  for (const [key, value] of urlParams) {
    params[key] = value;
  }
  
  // Define routes. 
  // FIX: Removed '/annonces/:id' to prevent conflict. 
  // We use '/annonces/:slug' for both IDs and Slugs.
  const routePatterns: { [key: string]: string[] } = {
    '/annonces/:slug': ['slug'], 
    '/listing/:id': ['id'], // Keep this for legacy/redirects
    '/actualite/:id': ['id'],
    '/actualite/:slug': ['slug'],
    '/neuf/:brandSlug': ['brandSlug'],
    '/neuf/:brandSlug/:modelSlug': ['brandSlug', 'modelSlug'],
    '/neuf/:brandSlug/:modelSlug/:versionSlug': ['brandSlug', 'modelSlug', 'versionSlug'],
    '/car-prices/:brandId': ['brandId'],
    '/car-prices/:brandId/:modelId': ['brandId', 'modelId'],
    '/car-prices/:brandId/:modelId/:versionId': ['brandId', 'modelId', 'versionId'],
    '/modifier-annonce/:id': ['id'], // Ensure this exists for editing
  };

  // Extract just the pathname part
  const pathname = currentPath.split('?')[0];
  
  // Check if current path matches any of our patterns
  for (const pattern in routePatterns) {
    const patternSegments = pattern.split('/');
    const currentSegments = pathname.split('/');

    if (patternSegments.length === currentSegments.length) {
      let isMatch = true;
      const extractedParams: Record<string, string> = {};

      for (let i = 0; i < patternSegments.length; i++) {
        if (patternSegments[i].startsWith(':')) {
          // This is a parameter, extract its value
          const paramName = patternSegments[i].substring(1);
          extractedParams[paramName] = currentSegments[i];
        } else if (patternSegments[i] !== currentSegments[i]) {
          // This segment doesn't match
          isMatch = false;
          break;
        }
      }

      if (isMatch) {
        Object.assign(params, extractedParams);
        break;
      }
    }
  }
  
  return params;
}